import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Plus, Trash, ArrowClockwise, MagnifyingGlass, ArrowsLeftRight, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));
const formatDateTime = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "\u2014" : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const STATUS_LABEL = {
  awaiting_production: "Awaiting Production",
  accepted: "Accepted - Stock Moved",
  rejected: "Rejected",
};
const STATUS_TONE = {
  awaiting_production: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  accepted: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  rejected: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
};

// Oct 27 2026, user's ask: "Issue for Production for the NPD to Move stock from
// the NPD warehouse to SFG warehouse... Stock movement trigger when production
// user accept it". NPD fills the output product it is for and the items out of
// its own godown; the site's production man accepts, and THAT is the moment SAP
// moves P1-NPD -> P1-SFG.
const OutputProductPicker = ({ value, name, onPick }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  const search = useCallback((q) => {
    clearTimeout(timer.current);
    if (!q || q.trim().length < 2) return setResults([]);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/npd-store-requests/material-search`, { params: { q: q.trim() } });
        setResults(data.materials || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  }, []);

  return (
    <div className="relative">
      <Input
        value={value}
        placeholder="Type the product code..."
        onChange={(e) => { onPick({ product_id: e.target.value.toUpperCase(), description: "", uom: "" }); search(e.target.value); }}
        className="h-9 font-mono"
        data-testid="npd-ifp-output-product-input"
      />
      {searching && <p className="text-[10px] text-[#667085] mt-0.5">Searching SAP...</p>}
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto bg-white border border-[#D0D5DD] rounded-sm shadow-md" data-testid="npd-ifp-output-results">
          {results.map((m) => (
            <button
              key={m.product_id}
              type="button"
              onClick={() => { onPick(m); setOpen(false); setResults([]); }}
              className="w-full text-left px-2 py-1.5 text-[12px] hover:bg-[#F0FDF9] border-b border-[#F2F4F7] last:border-b-0"
              data-testid={`npd-ifp-output-option-${m.product_id}`}
            >
              <span className="font-mono font-bold text-[#175CD3]">{m.product_id}</span>
              <span className="text-[#667085]"> &middot; {m.description || "No description"}</span>
            </button>
          ))}
        </div>
      )}
      {name && <p className="text-[11px] text-[#027A48] mt-0.5" data-testid="npd-ifp-output-name">{name}</p>}
    </div>
  );
};

export const NpdIssueForProductionTab = ({ sites, canRaise = true, allowDecide = false }) => {
  const [siteId, setSiteId] = useState("");
  const [apiSites, setApiSites] = useState([]);
  const [stock, setStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([]);
  const [output, setOutput] = useState({ product_id: "", description: "", uom: "" });
  const [outputQty, setOutputQty] = useState("");
  const [remark, setRemark] = useState("");
  const [customer, setCustomer] = useState("");
  const [sending, setSending] = useState(false);
  const [issues, setIssues] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");

  // A production-only user has no NPD right, so the page never loads the site
  // list for him - the queue endpoint hands back the sites he may see.
  const siteOptions = useMemo(() => (sites?.length ? sites : apiSites), [sites, apiSites]);
  useEffect(() => { if (!siteId && siteOptions.length) setSiteId(siteOptions[0]); }, [siteOptions, siteId]);

  const loadStock = useCallback(async (site) => {
    if (!site || !canRaise) return;
    setLoadingStock(true);
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/consumption/stock`, { params: { site_id: site } });
      setStock(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the NPD godown");
      setStock(null);
    } finally {
      setLoadingStock(false);
    }
  }, [canRaise]);

  const loadIssues = useCallback(async (site) => {
    try {
      const { data } = await axios.get(`${API}/npd-production-issues`, { params: { site_id: site || "" } });
      setIssues(data.issues || []);
      setApiSites(data.sites || []);
    } catch {
      setIssues([]);
    }
  }, []);

  useEffect(() => {
    loadStock(siteId);
    loadIssues(siteId);
    setLines([]);
  }, [siteId, loadStock, loadIssues]);

  const visibleStock = useMemo(() => {
    const rows = stock?.items || [];
    const q = search.trim().toUpperCase();
    return q ? rows.filter((r) => r.product_id.toUpperCase().includes(q) || (r.description || "").toUpperCase().includes(q)) : rows;
  }, [stock, search]);

  const addLine = (row) => {
    if (lines.some((l) => l.product_id === row.product_id)) return toast.error(`${row.product_id} is already on the list`);
    setLines((prev) => [...prev, {
      product_id: row.product_id, description: row.description, available: row.qty, uom: row.uom, quantity: String(row.qty),
    }]);
  };

  const missingField = useMemo(() => {
    if (!siteId) return "Select a site";
    if (!output.product_id) return "Pick the output product";
    if (!output.description) return "Pick the output product from the list so its name is filled";
    if (!(Number(outputQty) > 0)) return "Enter the output product quantity";
    if (lines.length === 0) return "Add at least one item from the NPD godown";
    if (lines.some((l) => !(Number(l.quantity) > 0))) return "Every line needs a quantity";
    const over = lines.find((l) => Number(l.quantity) > l.available);
    if (over) return `${over.product_id}: only ${formatQty(over.available)} ${formatUnit(over.uom)} in the godown`;
    return "";
  }, [siteId, output, outputQty, lines]);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/npd-production-issues`, {
        site_id: siteId,
        output_product_id: output.product_id,
        output_product_name: output.description,
        output_quantity: Number(outputQty),
        output_uom: output.uom || "",
        remark: remark.trim(),
        customer_name: customer.trim(),
        lines: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), uom: l.uom, description: l.description })),
      });
      toast.success(`${data._id} sent to ${siteId} production`, { description: "Stock moves in SAP only when production accepts it." });
      setLines([]);
      setOutput({ product_id: "", description: "", uom: "" });
      setOutputQty("");
      setRemark("");
      setCustomer("");
      loadIssues(siteId);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not raise this issue");
    } finally {
      setSending(false);
    }
  };

  const accept = async (row) => {
    setBusyId(row._id);
    try {
      const data = (await axios.post(`${API}/npd-production-issues/${row._id}/decide`, { decision: "accept" })).data;
      toast.success(`${row._id} accepted - stock moved into ${row.site_id}-SFG`, {
        description: data.sap_external_id ? `SAP movement ${data.sap_external_id}` : undefined,
      });
      loadIssues(siteId);
      loadStock(siteId);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "SAP refused the movement");
    } finally {
      setBusyId("");
    }
  };

  const reject = async () => {
    if (rejectRemark.trim().length < 5) return toast.error("Say why production is not taking it");
    setBusyId(rejectTarget._id);
    try {
      await axios.post(`${API}/npd-production-issues/${rejectTarget._id}/decide`, { decision: "reject", remark: rejectRemark.trim() });
      toast.success(`${rejectTarget._id} rejected - nothing moved in SAP`);
      setRejectTarget(null);
      setRejectRemark("");
      loadIssues(siteId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject this issue");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-5" data-testid="npd-issue-for-production-tab">
      {canRaise && (
      <div className="bg-white border border-[#D0D5DD] rounded-sm">
        <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3 flex-wrap">
          <ArrowsLeftRight size={16} className="text-[#0E7C86]" />
          <h2 className="text-sm font-bold text-[#101828]">Issue For Production</h2>
          <Badge variant="outline" className="text-[10px] bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]" data-testid="npd-ifp-flow-badge">
            PRODUCTION ACCEPTS, THEN SAP POSTS
          </Badge>
          <span className="text-xs text-[#667085]" data-testid="npd-ifp-context">
            {stock ? `${stock.godown} \u2192 ${siteId}-SFG (production)` : "\u2014"}
            {stock?.as_of ? ` \u00b7 stock as of ${formatDateTime(stock.as_of)}` : ""}
          </span>
          <Button variant="outline" size="sm" className="h-8 bg-white ml-auto" onClick={() => loadStock(siteId)} data-testid="npd-ifp-refresh-stock">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh Stock
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Site *</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger className="h-9 bg-white" data-testid="npd-ifp-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {siteOptions.map((s) => <SelectItem key={s} value={s} data-testid={`npd-ifp-site-option-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Output Product *</Label>
              <OutputProductPicker value={output.product_id} name={output.description} onPick={setOutput} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Output Product Qty *</Label>
              <Input
                type="number" min="0" step="any" value={outputQty}
                onChange={(e) => setOutputQty(e.target.value)}
                placeholder="Qty to be produced"
                className="h-9 text-right tabular-nums"
                data-testid="npd-ifp-output-qty-input"
              />
              <p className="text-[11px] text-[#667085]">{output.uom ? formatUnit(output.uom) : "unit comes from the product"}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Purpose</Label>
              <div className="h-9 flex items-center px-3 bg-[#F9FAFB] border border-[#EAECF0] rounded-sm text-[12px] font-bold text-[#475467]" data-testid="npd-ifp-purpose">
                Production
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Customer</Label>
              <Input
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Who this trial is for"
                className="h-9"
                data-testid="npd-ifp-customer-input"
              />
            </div>
            <div className="space-y-1 sm:col-span-2 lg:col-span-3">
              <Label className="text-xs font-bold text-[#344054]">Remark</Label>
              <Textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={2}
                placeholder="Anything production should know - trial number, drawing revision, urgency"
                data-testid="npd-ifp-remark-input"
              />
              <p className="text-[11px] text-[#667085]">Production sees the remark and the customer on this request.</p>
            </div>
          </div>

          <div className="bg-[#F0FDF9] border border-[#AAF0D1] rounded-sm px-3 py-2 text-[12px] text-[#027A48]" data-testid="npd-ifp-flow-note">
            {siteId} production checks this against their plan and accepts it - only then does the stock move in SAP, from{" "}
            <strong>{stock?.godown || `${siteId}-NPD`}</strong> into <strong>{siteId}-SFG</strong>. The output product is recorded as the reason, nothing is posted against it.
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-[#D0D5DD] rounded-sm">
              <div className="px-3 py-2 border-b border-[#EAECF0] flex items-center gap-2">
                <MagnifyingGlass size={14} className="text-[#667085]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search the NPD godown..."
                  className="h-7 border-0 shadow-none focus-visible:ring-0 px-0"
                  data-testid="npd-ifp-stock-search"
                />
                <span className="text-[11px] text-[#667085] whitespace-nowrap">{visibleStock.length} item(s)</span>
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-[12px]" data-testid="npd-ifp-stock-table">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      {["Product", "Description", "In Godown", ""].map((h) => (
                        <th key={h} className="px-2 py-1.5 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingStock && <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]">Reading the godown...</td></tr>}
                    {!loadingStock && visibleStock.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]" data-testid="npd-ifp-stock-empty">
                        {stock?.godown ? `${stock.godown} is empty - there is nothing to issue.` : "Select a site"}
                      </td></tr>
                    )}
                    {visibleStock.map((r) => (
                      <tr key={r.product_id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-ifp-stock-row-${r.product_id}`}>
                        <td className="px-2 py-1.5 font-mono font-bold">{r.product_id}</td>
                        <td className="px-2 py-1.5 text-[#475467]">{r.description || "\u2014"}</td>
                        <td className="px-2 py-1.5 tabular-nums">{formatQty(r.qty)} {formatUnit(r.uom)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button variant="outline" size="sm" className="h-6 bg-white text-[11px]" onClick={() => addLine(r)} data-testid={`npd-ifp-add-${r.product_id}`}>
                            <Plus size={12} className="mr-1" /> Add
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border border-[#D0D5DD] rounded-sm">
              <div className="px-3 py-2 border-b border-[#EAECF0]">
                <h3 className="text-xs font-bold text-[#344054]">Issuing To Production</h3>
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-[12px]" data-testid="npd-ifp-lines-table">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      {["Product", "In Godown", "Issue Qty", ""].map((h) => (
                        <th key={h} className="px-2 py-1.5 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]" data-testid="npd-ifp-lines-empty">Add items from the godown on the left.</td></tr>
                    )}
                    {lines.map((l, i) => (
                      <tr key={l.product_id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-ifp-line-row-${l.product_id}`}>
                        <td className="px-2 py-1.5 font-mono font-bold">{l.product_id}</td>
                        <td className="px-2 py-1.5 tabular-nums text-[#475467]">{formatQty(l.available)} {formatUnit(l.uom)}</td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number" min="0" step="any" value={l.quantity}
                            onChange={(e) => setLines((prev) => prev.map((x, xi) => (xi === i ? { ...x, quantity: e.target.value } : x)))}
                            className={`h-7 w-28 ${Number(l.quantity) > l.available ? "border-[#FDA29B]" : ""}`}
                            data-testid={`npd-ifp-qty-input-${l.product_id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button variant="outline" size="sm" className="h-6 bg-white text-[#B42318]" onClick={() => setLines((prev) => prev.filter((_, xi) => xi !== i))} data-testid={`npd-ifp-remove-${l.product_id}`}>
                            <Trash size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button type="button" disabled={!!missingField || sending} onClick={submit} data-testid="npd-ifp-submit-button">
              {sending ? "Sending..." : "Send To Production"}
            </Button>
            <span className="text-xs text-[#667085]" data-testid="npd-ifp-hint">
              {missingField || `${lines.length} line(s) ready \u00b7 ${siteId} production will accept them`}
            </span>
          </div>
        </div>
      </div>
      )}

      <div className="bg-white border border-[#D0D5DD] rounded-sm">
        <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3">
          <h2 className="text-sm font-bold text-[#101828]">{allowDecide ? "Issue From NPD - Awaiting You" : "Issue For Production Queue"}</h2>
          <span className="text-xs text-[#667085]">
            {allowDecide
              ? "Accepting moves the stock in SAP, out of the NPD godown into your SFG warehouse"
              : "Production accepts from their own page - that is the moment SAP moves the stock"}
          </span>
          <Button variant="outline" size="sm" className="h-8 bg-white ml-auto" onClick={() => loadIssues(siteId)} data-testid="npd-ifp-refresh-list">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="npd-ifp-list-table">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {["Request ID", "Raised", "Site", "Output Product", "Customer & Remark", "Items", "Status", "SAP Movement", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {issues.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[#98A2B3]" data-testid="npd-ifp-list-empty">Nothing issued for production yet.</td></tr>
              )}
              {issues.map((r) => (
                <tr key={r._id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-ifp-row-${r._id}`}>
                  <td className="px-3 py-2 font-mono font-bold text-[#175CD3]">{r._id}</td>
                  <td className="px-3 py-2 text-[#475467]">{formatDateTime(r.created_at)}<div className="text-[11px] text-[#98A2B3]">{r.requester}</div></td>
                  <td className="px-3 py-2">{r.site_id}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono font-bold">{r.output_product_id}</span>
                    <div className="text-[11px] text-[#667085]">{r.output_product_name}</div>
                    <div className="text-[11px] text-[#475467]">{formatQty(r.output_quantity)} {formatUnit(r.output_uom)}</div>
                  </td>
                  <td className="px-3 py-2 max-w-[220px]" data-testid={`npd-ifp-remark-${r._id}`}>
                    {r.customer_name && <span className="block font-bold text-[#344054]">{r.customer_name}</span>}
                    <span className="text-[#475467]">{r.remark || (r.customer_name ? "" : "\u2014")}</span>
                  </td>
                  <td className="px-3 py-2">
                    {(r.items || []).map((it) => (
                      <span key={it.product_id} className="block font-mono">
                        {it.product_id} · {formatQty(it.quantity)} {formatUnit(it.unit_of_measure)}
                        {(it.sap_goods_movement || {}).ok === false && (
                          <span className="block text-[11px] text-[#B42318] font-sans" data-testid={`npd-ifp-line-error-${r._id}-${it.product_id}`}>
                            {it.sap_goods_movement.error || it.sap_goods_movement.reason}
                          </span>
                        )}
                      </span>
                    ))}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[r.status] || ""}`} data-testid={`npd-ifp-status-${r._id}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </Badge>
                    {r.status === "rejected" && r.decision_remark && (
                      <span className="block text-[11px] text-[#B42318] mt-1" data-testid={`npd-ifp-reject-remark-${r._id}`}>
                        Production rejected: {r.decision_remark}
                      </span>
                    )}
                    {r.status === "accepted" && r.decided_by && (
                      <span className="block text-[11px] text-[#027A48] mt-1">Accepted by {r.decided_by}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[#027A48]">{r.sap_external_id || "\u2014"}</td>
                  <td className="px-3 py-2">
                    {allowDecide && r.can_decide && (
                      <div className="flex gap-1.5">
                        <Button size="sm" className="h-7" disabled={busyId === r._id} onClick={() => accept(r)} data-testid={`npd-ifp-accept-${r._id}`}>
                          <CheckCircle size={13} className="mr-1" />
                          {busyId === r._id ? "Moving..." : "Accept"}
                        </Button>
                        <Button
                          size="sm" variant="outline" className="h-7 bg-white text-[#B42318] border-[#FECDCA]"
                          disabled={busyId === r._id}
                          onClick={() => { setRejectTarget(r); setRejectRemark(""); }}
                          data-testid={`npd-ifp-reject-${r._id}`}
                        >
                          <XCircle size={13} className="mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) { setRejectTarget(null); setRejectRemark(""); } }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="npd-ifp-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Reject {rejectTarget?._id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#475467]">
              Nothing has moved in SAP, so the material stays in the NPD godown. NPD sees your remark and can raise it again.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-[#344054]">Why is production not taking it?</Label>
              <Textarea
                value={rejectRemark}
                onChange={(e) => setRejectRemark(e.target.value)}
                rows={3}
                placeholder="e.g. trial not scheduled this week, wrong grade for this part"
                data-testid="npd-ifp-reject-remark-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} data-testid="npd-ifp-reject-cancel">Keep It Pending</Button>
            <Button className="bg-[#B42318] hover:bg-[#912018]" disabled={!!busyId} onClick={reject} data-testid="npd-ifp-reject-confirm">
              {busyId ? "Rejecting..." : "Reject This Issue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
